import {observer} from 'mobx-react-lite'
import React, {useLayoutEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Button} from '@components/common/base/buttons'
import {Popup} from '@components/common/base/popup'
import {useForceUpdate, useGlobalKeyDown, useMemoOne} from '@containers/hooks'
import {InputNew} from '../../../common/base/input-new'
import {MessageBox} from '../../../message-box'
import {
  CompanyInviteRoleUserPopupContext,
  CompanyInviteRoleUserPopupStore,
  useCompanyInviteRoleUserPopupStore
} from './company-invite-role-user-store'
import {ProjectList} from './project-list'

const TwContent = tw.div`
  flex
  flex-col
  pt-8
  pb-24
  gap-20
  h-full
`

const TwCaption = tw.div`
  text-primary
  h2-semibold
  cursor-default
`

interface Props {
  close(): void
}

interface FooterProps {
  close(): void
  submit(): void
  canSubmit(): boolean
  submitDisabled?: boolean
}

const Footer = observer((props: FooterProps) => {
  const {submit, close, canSubmit, submitDisabled} = props
  return (
    <div className="flex items-center justify-end gap-12">
      <Button color="secondary" size="medium" onClick={close}>
        {Lang.msg.cancel}
      </Button>
      <Button
        color="primary"
        size="medium"
        onClick={submit}
        disabled={!canSubmit() || submitDisabled}
      >
        {Lang.msg.invitePopup.submitButton}
      </Button>
    </div>
  )
})

export const CompanyInviteRoleUserPopup = (props: Props) => {
  const store = useMemoOne(() => new CompanyInviteRoleUserPopupStore(), [])

  useLayoutEffect(() => {
    store.syncProjects()
  }, [])

  return (
    <CompanyInviteRoleUserPopupContext.Provider value={store}>
      <CompanyInvitePopupBase {...props} />
    </CompanyInviteRoleUserPopupContext.Provider>
  )
}

const CompanyInvitePopupBase = observer((props: Props) => {
  const [, store] = useCompanyInviteRoleUserPopupStore()
  const [isWaiting, setWaiting] = useState(false)
  const forceUpdate = useForceUpdate()

  const onClose = () => {
    props.close()
  }

  const onSubmit = async () => {
    if (isWaiting) {
      return
    }

    setWaiting(true)

    const result = await store.onSubmit()
    if (result) {
      MessageBox.success(Lang.msg.invitePopup.roleCreated)
      props.close()
    } else {
      MessageBox.danger(Lang.msg.invitePopup.cannotCreateRole)
    }

    setWaiting(false)
    forceUpdate()
  }

  useGlobalKeyDown({
    escape: () => {
      onClose()
      return true
    },
    anyKey: e => {
      if (e.key === 'Enter' && !e.shift) {
        void onSubmit()
        return true
      }
      return false
    }
  })

  return (
    <Popup
      close={onClose}
      title={Lang.msg.invitePopup.addingVirtualUser}
      width={600}
      minHeight={500}
      autoHeight
      noScroll={true}
      footer={
        <Footer
          close={props.close}
          submit={onSubmit}
          canSubmit={store.canSubmit}
          submitDisabled={isWaiting}
        />
      }
    >
      <TwContent>
        <div className="pb-8">
          <InputNew
            autoFocus
            size="md"
            label={Lang.msg.invitePopup.roleInCompany}
            onChange={store.setEmails}
            value={store.name}
          />
        </div>

        <div className="flex flex-col gap-16">
          <TwCaption>{Lang.msg.invitePopup.addToProjects}</TwCaption>
          <ProjectList />
        </div>
      </TwContent>
    </Popup>
  )
})
